import React, { useState } from 'react';
import { AiComponentJsVariable } from '@/pkg/ai';
import { StackedInputGroupWrapper } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/shared';
import { GenericJsStringInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/js/js-string-input';
import { GenericJsNumberInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/js/js-number-input';
import { GenericJsBooleanInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/js/js-boolean-input';
import { useOnChangeDebounced } from '@/components/hooks/use-on-change';
import { GenericJsObjectInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/js/js-object-input';
import { GenericJsRecsStrategyInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/js/js-recs-strategy-input';
import { GenericJsCollectionsStrategyInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/js/js-collections-strategy-input';
import { GenericJsTimezoneInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/js/js-timezone-input';

export interface GenericJsVariableInputProps {
  component: AiComponentJsVariable;
  getJsVariables?: () => AiComponentJsVariable[];
  setJsVariable?: (variable: AiComponentJsVariable) => void;
}

export function GenericJsVariableInput({
  component,
  getJsVariables,
  setJsVariable,
}: GenericJsVariableInputProps) {
  const [variable, setVariable] = useState(
    getJsVariables().find((v) => v.variable === component.variable),
  );

  const onChange = (value: string) => {
    setVariable((prev) => ({ ...prev, value }));
  };

  useOnChangeDebounced(
    () => {
      setJsVariable?.(variable);
    },
    500,
    [variable],
  );

  const isObject = [`object`, `array`].includes(variable.variableType);
  const isRecsConfig =
    isObject && variable.value.includes(`recommendationOptions`);
  const isCollectionsStrategy =
    variable.variableType === `string` &&
    variable.selector?.toLowerCase().includes(`strategy`) &&
    [`o`, `v`].includes(variable.value);
  const isTimezone =
    variable.variableType === `string` &&
    variable.selector?.toLowerCase().includes(`timezone`);

  return (
    <StackedInputGroupWrapper layout style={{ padding: 0 }}>
      {variable.variableType === `string` &&
        !isCollectionsStrategy &&
        !isTimezone && (
          <GenericJsStringInput value={variable.value} onChange={onChange} />
        )}
      {isCollectionsStrategy && (
        <GenericJsCollectionsStrategyInput
          value={variable.value}
          onChange={onChange}
        />
      )}
      {isTimezone && (
        <GenericJsTimezoneInput value={variable.value} onChange={onChange} />
      )}
      {variable.variableType === `number` && (
        <GenericJsNumberInput value={variable.value} onChange={onChange} />
      )}
      {variable.variableType === `boolean` && (
        <GenericJsBooleanInput value={variable.value} onChange={onChange} />
      )}
      {isObject && !isRecsConfig && (
        <GenericJsObjectInput value={variable.value} onChange={onChange} />
      )}
      {isObject && isRecsConfig && (
        <GenericJsRecsStrategyInput
          variableName={variable.selector}
          value={variable.value}
          onChange={onChange}
        />
      )}
    </StackedInputGroupWrapper>
  );
}
